/**
 * Helpers puros para navegar na árvore de módulos/submódulos do curso.
 *
 * A API de estatísticas devolve `modules` como árvore (campo `children`).
 * Cursos antigos, de um nível só, chegam com `children` vazio e continuam
 * funcionando normalmente: a travessia DFS degenera na lista plana original.
 */

interface TreeLesson {
  id: number;
  status: 'completed' | 'in_progress' | 'locked' | 'none';
}

export interface ModuleTreeNode<L extends TreeLesson = TreeLesson> {
  id: number;
  classes: L[];
  children: ModuleTreeNode<L>[];
}

/** Travessia DFS (pré-ordem): módulo, suas aulas, depois seus submódulos. */
export function flattenModuleTree<T extends ModuleTreeNode>(modules: T[]): T[] {
  return modules.flatMap((mod) => [mod, ...flattenModuleTree(mod.children as T[])]);
}

/** Total de aulas no curso inteiro, incluindo as de submódulos. */
export function countClassesInTree(modules: ModuleTreeNode[]): number {
  return flattenModuleTree(modules).reduce((acc, mod) => acc + mod.classes.length, 0);
}

/** Posição (0-based) da aula na ordem DFS; 0 se não encontrada. */
export function indexOfClassInTree(modules: ModuleTreeNode[], classId?: number): number {
  if (classId == null) return 0;
  let count = 0;
  for (const mod of flattenModuleTree(modules)) {
    const idx = mod.classes.findIndex((lesson) => lesson.id === classId);
    if (idx !== -1) return count + idx;
    count += mod.classes.length;
  }
  return 0;
}

export interface ModuleProgress {
  completed: number;
  total: number;
  /** 0-100, arredondado para baixo para não exibir 100% antes de concluir todas as aulas. */
  percent: number;
}

/** Progresso consolidado do módulo: aulas próprias + as de todos os submódulos. */
export function getModuleProgress(module: ModuleTreeNode): ModuleProgress {
  const lessons = flattenModuleTree([module]).flatMap((mod) => mod.classes);
  const total = lessons.length;
  const completed = lessons.filter((lesson) => lesson.status === 'completed').length;
  const percent = total > 0 ? Math.floor((completed / total) * 100) : 0;
  return { completed, total, percent };
}

export interface FirstAvailableResult<L extends TreeLesson> {
  classItem: L | null;
  /** ids do módulo que contém a aula e de todos os seus ancestrais, para expandir o caminho. */
  modulePathIds: number[];
}

/**
 * Primeira aula não bloqueada na ordem DFS, junto com o caminho de módulos
 * (ancestrais + módulo dono) que precisa ficar expandido para exibi-la.
 */
export function findFirstAvailableClass<L extends TreeLesson>(
  modules: ModuleTreeNode<L>[],
  ancestors: number[] = [],
): FirstAvailableResult<L> {
  for (const mod of modules) {
    const path = [...ancestors, mod.id];
    const classItem = mod.classes.find((lesson) => lesson.status !== 'locked');
    if (classItem) return { classItem, modulePathIds: path };

    const fromChildren = findFirstAvailableClass(mod.children, path);
    if (fromChildren.classItem) return fromChildren;
  }
  return { classItem: null, modulePathIds: [] };
}
